import React from 'react';
import { useHistory } from 'react-router-dom';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import { safeRedirectUrl } from '../../Organization/types';

interface Stage {
    id: number;
    title: string;
    description: string;
    status: 'completed' | 'pending' | 'current';
    path: string;
    url?: string | null;  // URL opcional para etapas especiales
}

interface StageItemProps {
    stage: Stage;
    isLast: boolean;
    onClick: () => void;
    verificationId?: number | null;
    verificationPaymentStatus?: string | null;
    isApplicant?: boolean;  // Indica si el usuario actual es el solicitante
    paymentUrl?: string | null;  // URL de pago (la misma que se envía en el correo)
    paymentManagedByFirm?: boolean;  // Caso de un estudio: el pago se acuerda fuera de Monere
}

const StageItem: React.FC<StageItemProps> = ({ stage, isLast, onClick, verificationId, verificationPaymentStatus, isApplicant, paymentUrl, paymentManagedByFirm }) => {
    const history = useHistory();
    
    const getStatusClass = () => {
        switch (stage.status) {
            case 'completed':
                return 'stage-completed';
            case 'current':
                return 'stage-current';
            default:
                return 'stage-pending';
        }
    };

    // Verificar si es la etapa 3 (Inicio solicitud INAPI), el usuario es el solicitante y tiene pago pendiente
    // En los casos de un estudio el pago lo acuerda el estudio con su cliente: no se cobra en Monere
    const isPaymentPending = !paymentManagedByFirm && stage.id === 3 && isApplicant && verificationId && verificationPaymentStatus === 'pending';
    
    // Para el solicitante con pago pendiente, la etapa sigue siendo clickable (current) pero muestra el botón de pago
    // Para otros usuarios, si hay pago pendiente, la etapa está pending y no es clickable
    const isClickable = stage.status === 'completed' || stage.status === 'current';

    const handleClick = () => {
        // Si es el solicitante y hay pago pendiente en etapa 3, redirigir a pagar
        if (isPaymentPending && paymentUrl) {
            window.location.href = safeRedirectUrl(paymentUrl);
            return;
        }
        
        if (isClickable) {
            onClick();
        }
    };
    
    const handlePaymentClick = () => {
        if (paymentUrl) {
            // Ir directamente a la URL de pago (la misma que se envía en el correo)
            window.location.href = safeRedirectUrl(paymentUrl);
        } else if (verificationId) {
            // Fallback: construir la URL si no viene del backend
            history.push(`/intranet/payment/brand-registration/summary/${verificationId}`);
        }
    };

    return (
        <div className={`stage-item ${getStatusClass()} ${!isClickable ? 'stage-disabled' : ''}`}>
            <div 
                className="stage-content" 
                onClick={handleClick}
                style={{ cursor: isClickable ? 'pointer' : 'not-allowed', opacity: isClickable ? 1 : 0.6 }}
            >
                <div className="stage-number">
                    <span className="number">{stage.id}</span>
                </div>
                
                <div className="stage-info" style={{ flex: 1 }}>
                    <h3 className="stage-title">{stage.title}</h3>
                    <p className="stage-description">{stage.description}</p>
                    
                    {/* Botón de pago si está pendiente */}
                    {isPaymentPending && (
                        <div className="mt-3">
                            <div className="alert alert-warning mb-2" style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                <i className="fas fa-exclamation-circle me-2"></i>
                                <strong>Pago pendiente:</strong> Debes completar el pago para continuar con el proceso.
                            </div>
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                title="Pagar solicitud INAPI"
                                onClick={handlePaymentClick}
                                icon="fa-credit-card"
                                size="small"
                            />
                        </div>
                    )}
                </div>
                
                {!isPaymentPending && (
                <div className="stage-arrow">
                    <i className="fas fa-chevron-right"></i>
                </div>
                )}
            </div>
            
            {!isLast && <div className="stage-divider"></div>}
        </div>
    );
};

export default StageItem;
